import axios from 'axios';
import { TRAVEL_PLANS_CONTENT_ENDPOINT, COVERS_CONTENT_ENDPOINT } from './baseURL';

/**
 * Fetch Travel Plans Content from Content API
 * Returns plan details including benefits for all plan types (Flexi, Gold, Platinum)
 * @returns {Promise<Object>} API response with plan details
 */
export const getTravelPlansContent = async () => {
  try {
    console.log('Fetching Travel Plans Content from:', TRAVEL_PLANS_CONTENT_ENDPOINT);

    const response = await axios.get(TRAVEL_PLANS_CONTENT_ENDPOINT, {
      timeout: 10000,
    });

    console.log('Travel Plans Content API Response:', response.data);

    if (response.data && response.data.data && response.data.data.length > 0) {
      const planData = response.data.data[0].attributes;

      return {
        success: true,
        data: {
          identifier: planData.identifier,
          locale: planData.locale,
          planDetails: planData.plan_details || [],
        },
        rawData: response.data,
      };
    }

    return {
      success: false,
      error: 'No plan data found',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching Travel Plans Content:', error);

    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch travel plans content',
      data: null,
    };
  }
};

/**
 * Transform plan details from API format to component-friendly format
 * @param {Array} planDetails - Array of plan detail objects from API
 * @param {string} currentLanguage - Current app language ('en' or 'ar')
 * @returns {Array} Transformed benefits array for the modal
 */
export const transformPlanDetailsForModal = (planDetails, currentLanguage = 'en') => {
  if (!planDetails || !Array.isArray(planDetails)) {
    return [];
  }

  return planDetails.map((detail) => {
    const isArabic = currentLanguage === 'ar';

    // Helper function to get value or N/A placeholder
    const getValue = (enValue, arValue) => {
      const value = isArabic ? arValue : enValue;

      // Check if value is N/A
      if (!value || value === 'N/A' || value === 'لا ينطبق' || value === 'N.A') {
        return '❌';
      }

      // Check if value is "Covered"
      if (value === 'Covered' || value === 'مشمولة') {
        return '✅';
      }

      return value;
    };

    return {
      label: detail.benefits_en || '',
      label_ar: detail.benefits_ar || detail.benefits_en || '',
      values: [
        getValue(detail.flexi_en, detail.flexi_ar),
        getValue(detail.gold_en, detail.gold_ar),
        getValue(detail.plat_en, detail.plat_ar),
      ],
    };
  });
};

// In-flight + result cache keyed by productCode. Dedupes concurrent calls
// (e.g. React 18 StrictMode double-mount in dev) so only one network request
// is ever issued per product code, and no caller can abort another caller's
// request. Successful results are cached so subsequent renders don't refetch.
const __coversInflight = new Map();
const __coversCache = new Map();

/**
 * Fetch product covers/benefits from Content API.
 * Fire-and-forget — never accepts an abort signal, never times out, and
 * dedupes concurrent callers, so it cannot appear as "cancelled" in the
 * network panel due to client-side abort.
 * @param {string} productCode - The product code (e.g., "0204", "0710")
 * @returns {Promise<Object>} { success: true/false, data: [{ en, ar }] }
 */
export const getProductCovers = async (productCode) => {
  if (!productCode) return { success: false, data: [] };

  if (__coversCache.has(productCode)) {
    return __coversCache.get(productCode);
  }
  if (__coversInflight.has(productCode)) {
    return __coversInflight.get(productCode);
  }

  const url = `${COVERS_CONTENT_ENDPOINT}?filters[ProductCode][$eq]=${productCode}&pLevel=7`;
  console.log('Fetching Product Covers from:', url);

  const promise = (async () => {
    try {
      // No signal, no timeout — request runs to completion regardless of
      // component lifecycle, so it cannot be cancelled by the client.
      const response = await axios.get(url);

      console.log('Product Covers API Response:', response.data);

      if (response.data?.data?.length > 0) {
        const entry = response.data.data[0];
        // Support both Strapi v4 (`{ id, attributes: { CoversList } }`) and
        // flat / v5 (`{ id, CoversList }`) response shapes.
        const coversList = entry.CoversList || entry.attributes?.CoversList || [];

        const benefits = coversList.flatMap((cover) =>
          (cover.CoverageDescription || []).map((desc) => ({
            en: desc.EnglishDescription || '',
            ar: desc.ArabicDescription || '',
          }))
        );

        const result = { success: true, data: benefits };
        __coversCache.set(productCode, result);
        return result;
      }

      return { success: false, data: [] };
    } catch (error) {
      console.error('Error fetching Product Covers:', error);
      return { success: false, data: [] };
    } finally {
      __coversInflight.delete(productCode);
    }
  })();

  __coversInflight.set(productCode, promise);
  return promise;
};

/**
 * Fetch product covers from the Content API but preserve the per-cover
 * identity (CoverCode + CoverName) so callers can match the Strapi covers
 * against the cover codes returned by the `policy/view` API.
 *
 * Reuses the same in-flight cache pattern as `getProductCovers` (separate
 * key) so concurrent renders only issue one network request per product
 * code.
 *
 * @param {string} productCode - The product code (e.g., "0101")
 * @returns {Promise<Object>} {
 *   success: boolean,
 *   data: [{
 *     coverCode: string,
 *     coverName: string,
 *     descriptions: [{ en, ar }]
 *   }]
 * }
 */
const __coversWithCodesCache = new Map();
const __coversWithCodesInflight = new Map();
export const getProductCoversWithCodes = async (productCode) => {
  if (!productCode) return { success: false, data: [] };
  if (__coversWithCodesCache.has(productCode)) {
    return __coversWithCodesCache.get(productCode);
  }
  if (__coversWithCodesInflight.has(productCode)) {
    return __coversWithCodesInflight.get(productCode);
  }

  const url = `${COVERS_CONTENT_ENDPOINT}?filters[ProductCode][$eq]=${productCode}&pLevel=7`;

  const promise = (async () => {
    try {
      const response = await axios.get(url);
      if (response.data?.data?.length > 0) {
        const entry = response.data.data[0];
        const coversList =
          entry.CoversList || entry.attributes?.CoversList || [];
        const data = coversList.map((cover) => ({
          coverCode: cover.CoverCode || cover.attributes?.CoverCode || "",
          coverName: cover.CoverName || cover.attributes?.CoverName || "",
          descriptions: (cover.CoverageDescription || []).map((desc) => ({
            en: desc.EnglishDescription || "",
            ar: desc.ArabicDescription || "",
          })),
        }));
        const result = { success: true, data };
        __coversWithCodesCache.set(productCode, result);
        return result;
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error("Error fetching Product Covers (with codes):", error);
      return { success: false, data: [] };
    } finally {
      __coversWithCodesInflight.delete(productCode);
    }
  })();

  __coversWithCodesInflight.set(productCode, promise);
  return promise;
};

export default {
  getTravelPlansContent,
  transformPlanDetailsForModal,
  getProductCovers,
  getProductCoversWithCodes,
};
